/**
 * Who answers for whom in a team (people in Team Work, workbench 0.89.20). AIOE's own contract; the workbench copies
 * it into `shared/schemas/teamDelegation.ts`. The team record AIOE stores carries it as `delegation`, edited through the
 * team save (portfolio and project routes, overlay rules as for every other field: a save that leaves it out keeps it;
 * `{ topics: [], cover: [] }` clears it).
 *
 *  - `topics`: what a question may be about, and the people who answer it, in order (the first is asked first). An
 *    agent that matches a question to a topic asks its first answerer and, after `waitMinutes` (or the team's
 *    `defaultWaitMinutes`), may decide to ask the next one; nothing moves on a timer by itself (teamworkMentions.ts).
 *  - `cover`: who may answer a question addressed to someone else: always, or while that person is marked away
 *    (`PUT /people/v1/me/availability`). An answer by a cover records whom it was for (`answeredFor`).
 *
 * Every answerer and every cover is one of the team's people: a human member naming the person (`subjectId`), or the
 * team's owner. A cover loop (Ana covered by Ben, Ben by Ana; or anyone covering themselves) is refused. The save
 * answers 400 `invalid_team` with these problems in words; the team check reports the same ones on a stored team.
 */
import { z } from "zod";
export const DELEGATION_WAIT_MIN = 5;
export const DELEGATION_WAIT_MAX = 10_080;
export const DELEGATION_TOPICS_MAX = 50;
export const DELEGATION_COVER_MAX = 100;
export const DELEGATION_PEOPLE_MAX = 20;
const subjectId = z.string().min(1).max(200);
const wait = z.number().int().min(DELEGATION_WAIT_MIN).max(DELEGATION_WAIT_MAX);
export const delegationTopicSchema = z.object({
    id: z.string().min(1).max(100),
    label: z.string().min(1).max(200),
    describe: z.string().max(300).optional(),
    /** In order: the first is asked first. */
    answerers: z.array(subjectId).min(1).max(DELEGATION_PEOPLE_MAX),
    waitMinutes: wait.optional(),
});
export const DELEGATION_COVER_WHEN = ["away", "always"];
export const delegationCoverSchema = z.object({
    /** The person covered. */
    person: subjectId,
    /** Who may answer for them. */
    by: z.array(subjectId).min(1).max(DELEGATION_PEOPLE_MAX),
    when: z.enum(DELEGATION_COVER_WHEN),
});
export const teamDelegationSchema = z.object({
    topics: z.array(delegationTopicSchema).max(DELEGATION_TOPICS_MAX).default([]),
    cover: z.array(delegationCoverSchema).max(DELEGATION_COVER_MAX).default([]),
    defaultWaitMinutes: wait.optional(),
});
/** The team's people, as delegation names them: its human members' subjects and its owner. */
export function teamPeople(team) {
    const out = new Set();
    for (const m of team.members)
        if (m.human && m.subjectId)
            out.add(m.subjectId);
    if (team.owner?.id)
        out.add(team.owner.id);
    return out;
}
/** A cover loop, as the people in it in order (the first again at the end), or undefined when there is none. */
export function coverLoop(d) {
    const edges = new Map();
    for (const c of d.cover) {
        const to = edges.get(c.person) ?? new Set();
        for (const b of c.by)
            to.add(b);
        edges.set(c.person, to);
    }
    const state = new Map();
    const path = [];
    const visit = (n) => {
        if (state.get(n) === 2)
            return undefined;
        if (state.get(n) === 1)
            return [...path.slice(path.indexOf(n)), n];
        state.set(n, 1);
        path.push(n);
        for (const m of edges.get(n) ?? []) {
            const loop = visit(m);
            if (loop)
                return loop;
        }
        path.pop();
        state.set(n, 2);
        return undefined;
    };
    for (const n of edges.keys()) {
        const loop = visit(n);
        if (loop)
            return loop;
    }
    return undefined;
}
/**
 * What is wrong with a team's delegation, in words: answerers and covers who are not among `people` (the team's people,
 * {@link teamPeople}), a cover loop, a topic id or a covered person given twice. `nameOf` words a person (their name, else
 * their id). Empty when it is sound.
 */
export function delegationProblems(d, people, nameOf = (id) => id) {
    const out = [];
    const outside = (ids) => [...new Set(ids.filter((x) => !people.has(x)))];
    const list = (ids) => (ids.length === 1 ? nameOf(ids[0]) : `${ids.slice(0, -1).map(nameOf).join(", ")} and ${nameOf(ids[ids.length - 1])}`);
    const topicIds = new Set();
    for (const t of d.topics) {
        if (topicIds.has(t.id))
            out.push({ code: "duplicate_topic", path: `topics.${t.id}`, ids: [], message: `The topic ${t.label} is listed twice.` });
        topicIds.add(t.id);
        const bad = outside(t.answerers);
        if (bad.length)
            out.push({ code: "not_a_member", path: `topics.${t.id}.answerers`, ids: bad, message: `${list(bad)} ${bad.length === 1 ? "answers" : "answer"} ${t.label} but ${bad.length === 1 ? "is not one" : "are not"} of the team's people.` });
    }
    const covered = new Set();
    for (const c of d.cover) {
        if (covered.has(c.person))
            out.push({ code: "duplicate_cover", path: `cover.${c.person}`, ids: [c.person], message: `${nameOf(c.person)} has two cover rules; keep one.` });
        covered.add(c.person);
        const bad = outside([c.person, ...c.by]);
        if (bad.length)
            out.push({ code: "not_a_member", path: `cover.${c.person}.by`, ids: bad, message: `${list(bad)} ${bad.length === 1 ? "is" : "are"} named in a cover rule but ${bad.length === 1 ? "is not one" : "are not"} of the team's people.` });
    }
    const loop = coverLoop(d);
    if (loop)
        out.push({ code: "cover_loop", path: "cover", ids: [...new Set(loop)], message: loop.length === 2 ? `${nameOf(loop[0])} cannot cover themselves.` : `The cover rules go round in a loop: ${loop.map(nameOf).join(" covered by ")}.` });
    return out;
}
/** Who may answer a question addressed to `person` now: the people who cover them always, and, while they are away, those who cover them then. */
export function coversOf(d, person, away) {
    if (!d)
        return [];
    const out = [];
    for (const c of d.cover)
        if (c.person === person && (c.when === "always" || away))
            for (const b of c.by)
                if (b !== person && !out.includes(b))
                    out.push(b);
    return out;
}
//# sourceMappingURL=teamDelegation.js.map